import React, {
	type RefObject,
	useCallback,
	useEffect,
	useLayoutEffect,
	useMemo,
	useRef,
} from 'react';

import { useIntl } from 'react-intl-next';

import { getDocument } from '@atlaskit/browser-apis';
import FeatureGates from '@atlaskit/feature-gate-js-client';
import { fg } from '@atlaskit/platform-feature-flags';
import { addUFOCustomData } from '@atlaskit/react-ufo/custom-data';
import traceUFOPress from '@atlaskit/react-ufo/trace-press';
import { addRecentSearch, RECENT_SEARCH_KEY } from '@atlassian/search-common';
import { useKeyboardHighlightProvider } from '@atlassian/search-dialog';

import { ApplicationModes } from '../../../common/constants/application-modes';
import {
	PrimaryProductKeys,
} from '../../../common/constants/products';
import {
	QUICK_FIND_CLOSE_INTERACTION,
	QUICK_FIND_OPEN_INTERACTION,
} from '../../../common/constants/quick-find';
import { useAppContext } from '../../../common/ui/app-context';
import {
	Textfield as DefaultTextfield,
	type TextfieldProps,
} from '../../../common/ui/quick-find/textfield';
import { useInputSkeleton } from '../../../common/ui/quick-find/textfield/input-skeleton';
import { onAdvancedSearchLinkSelected } from '../../../common/utils/quick-find/events/advanced-search-link-selected';
import { onGhostTextShownEvent } from '../../../common/utils/quick-find/events/ghost-text-shown';
import {
	GhostTextExperimentCohort,
	parseGhostText,
} from '../../../common/utils/quick-find/ghost-text';
import { getWorkItemPageUrl } from '../../../common/utils/quick-find/jira-work-item-navigation';
import { State } from '../../../common/utils/quick-find/state';
import { isOnJiraFullPageSearch } from '../../../common/utils/url';
import { useLogException } from '../../../controllers/error-boundary';
import { useSelectedFilters } from '../../../controllers/filters/selected';
import {
	useQuickFindAnalytics,
	useQuickFindAttributes,
} from '../../../controllers/quick-find/analytics';
import { getQueryWithoutSelectedQueryFilters } from '../../../controllers/quick-find/query-filters/utils';
import { useQuickFindPostQueryVitalStats } from '../../../controllers/quick-find/use-quickfind-post-query-vitalstats';
import {
	useApplicationMode,
	useBootstrap,
	useSearchActions,
	useSelectedProduct,
} from '../../../controllers/store';
import {
	useContent,
	useQuickFindActions,
	useQuickFindOpen,
	useQuickFindQuery,
	useSelectedQueryFilters,
	useUpdateRecentQueries,
} from '../../../controllers/store/quick-find';
// eslint-disable-next-line @atlassian/tangerine/import/no-parent-imports
import { preloadSearchPageQuery } from '../../search-page-relay/utils/query-loader';


import { generateGhostTextData } from './ghost-text';
import { useQueryFilters } from './query-filters';
import { useDebouncedOnTextEnteredAnalyticsCallback } from './utils';


export type SearchTextFieldProps = {
	dialogId: string;
	inputRef?: RefObject<HTMLInputElement>;
	localQuery: string;
	setLocalQuery: React.Dispatch<React.SetStateAction<string>>;
	textfieldComponent?: React.ComponentType<TextfieldProps>;
	postQueryDisabled?: boolean;
	customPlaceholder?: string;
	onGhostTextGenerated?: (ghostText: string) => void;
};

export const SearchTextField = ({
	dialogId,
	inputRef,
	localQuery,
	setLocalQuery,
	textfieldComponent,
	postQueryDisabled,
	customPlaceholder,
	onGhostTextGenerated,
}: SearchTextFieldProps): React.JSX.Element => {
	const previousTextQueryParam = useRef<string | boolean | string[] | null | undefined>();

	const { generateSearchUrl, onNavigate, queryParams, isNav4Enabled, isAdminHubAIEnabled } =
		useAppContext();
	const { formatMessage } = useIntl();

	const applicationMode = useApplicationMode();
	const [{ primaryProduct, user, thirdPartyConfigs, cloudId, products }] = useBootstrap();
	const content = useContent();
	const quickFindOpen = useQuickFindOpen();
	const quickFindQuery = useQuickFindQuery();
	const keyboardHighlight = useKeyboardHighlightProvider();
	const getSelectedIndex = keyboardHighlight?.getSelectedIndex;
	const updateRecentQueries = useUpdateRecentQueries();
	const selectedQueryFilters = useSelectedQueryFilters();
	const [selectedProduct] = useSelectedProduct();
	const {
		selectedFilters: { jiraFilters },
	} = useSelectedFilters({
		thirdPartyConfigs,
		cloudId,
	});

	const { setQuickFindOpen, resetSelectedQueryFilters } = useQuickFindActions();
	const { setQueryUpdatedTime, clearAllFilters, setInputQuery } = useSearchActions();

	const { fireAnalyticsEvent } = useQuickFindAnalytics();
	const { commonAttributes, nonPrivacySafeAttributes } = useQuickFindAttributes();

	const debouncedOnTextEnteredAnalyticsCallback = useDebouncedOnTextEnteredAnalyticsCallback();

	const logException = useLogException();
	const { abortTask, isTaskActive } = useQuickFindPostQueryVitalStats();

	useInputSkeleton({
		localQuery,
		inputRef,
	});

	const isEligible3PUser = Object.keys(thirdPartyConfigs).some(
		(productKey) => thirdPartyConfigs[productKey]?.userNeedsOAuth === false,
	);

	const serve3PGhostTextExperiment = isEligible3PUser
		&& fg('quickfind-ghosttext')
		&& FeatureGates.getExperimentValue<string>(
			'confluence_quick_find_ghost_text_3p_experiment',
			'cohort',
			GhostTextExperimentCohort.CONTROL
		) === GhostTextExperimentCohort.THREE_P;

	// Parse existing ghost text data to get current state
	const existingGhostTextData = useMemo(() => {
		return customPlaceholder ? parseGhostText(customPlaceholder) : undefined;
	}, [customPlaceholder]);

	useEffect(() => {
		if (!fg('quickfind-ghosttext') || !serve3PGhostTextExperiment) {
			return;
		}

		const newGhostTextData = generateGhostTextData({
			products,
			thirdPartyConfigs,
			primaryProduct,
			formatMessage,
			existingGhostTextData,
		});

		if (onGhostTextGenerated) {
			onGhostTextGenerated(JSON.stringify(newGhostTextData));
		}
	}, [serve3PGhostTextExperiment, products, thirdPartyConfigs, primaryProduct, formatMessage, existingGhostTextData, onGhostTextGenerated]);

	useEffect(() => {
		if (existingGhostTextData && fg('quickfind-ghosttext')) {
			fireAnalyticsEvent(onGhostTextShownEvent({
				attributes: {
					...commonAttributes,
					customGhostText: existingGhostTextData.text,
					customGhostTextCatagory: existingGhostTextData.category,
				},
				nonPrivacySafeAttributes,
			}));
		}
	}, [existingGhostTextData, fireAnalyticsEvent, commonAttributes, nonPrivacySafeAttributes]);

	/**
	 * When Quick Find is SSR'd, users may type into the input before the
	 * React component is mounted. Given that useLayoutEffect is called before
	 * the first browser paint but after the input ref is set, this essentially
	 * initialises the React component with the input's state prior to hydration.
	 * - Input in the DOM element is reflected in localQuery
	 * - Focus state is preserved as hydration causes the input to lose focus
	 *
	 * It should be noted that re-renders caused state by updates in useLayoutEffect
	 * are synchronous, i.e. occurs before the paint, hence this is TTVC friendly.
	 *
	 * When this component is not SSR'd, this effectively does nothing.
	 */
	useLayoutEffect(() => {
		if (inputRef?.current) {
			setLocalQuery(inputRef.current.value);

			const document = getDocument();
			const isFocused = document && document.activeElement === inputRef.current;
			const selectionStart = isFocused ? inputRef.current.selectionStart : null;
			const selectionEnd = isFocused ? inputRef.current.selectionEnd : null;

			if (isFocused) {
				requestAnimationFrame(() => {
					inputRef?.current?.focus();
					if (selectionStart !== null && selectionEnd !== null) {
						inputRef?.current?.setSelectionRange(selectionStart, selectionEnd);
					}
				});
			}
		}
	}, [inputRef, setLocalQuery]);

	useQueryFilters(localQuery, postQueryDisabled);

	// Only when the text query param changes, update the local query. If the
	// local query without selected query filters is the same as text query param,
	// do nothing.
	useEffect(() => {
		if (queryParams.text === previousTextQueryParam.current) {
			return;
		}

		previousTextQueryParam.current = queryParams.text;

		const localQueryWithoutSelectedQueryFilters = getQueryWithoutSelectedQueryFilters(
			localQuery,
			selectedQueryFilters,
		);

		const newLocalQuery = typeof queryParams.text === 'string' ? queryParams.text : '';

		if (localQueryWithoutSelectedQueryFilters !== newLocalQuery) {
			setInputQuery(newLocalQuery);
			setLocalQuery(newLocalQuery);
			clearAllFilters();
			resetSelectedQueryFilters();
		}
	}, [
		clearAllFilters,
		queryParams.text,
		resetSelectedQueryFilters,
		selectedQueryFilters,
		setLocalQuery,
		localQuery,
		setInputQuery,
	]);

	// Update query time when query changes
	useEffect(() => {
		setQueryUpdatedTime();
	}, [setQueryUpdatedTime, localQuery]);

	useEffect(() => {
		if (quickFindOpen) {
			inputRef?.current?.focus();
		} else {
			inputRef?.current?.blur();
		}
	}, [inputRef, quickFindOpen]);

	const onKeyDown = useCallback(
		async (event: React.KeyboardEvent<HTMLInputElement>) => {
			/**
			 * Ignore enter key press if a search result is highlighted as we let
			 * the keyboard highlight provider handle the event in this case.
			 */
			const resultHighlighted = getSelectedIndex && getSelectedIndex() !== undefined;

			if (event.key === 'Enter' && !resultHighlighted) {
				/**
				 * Prevent IME composition from submitting the search
				 */
				if (event.nativeEvent.isComposing) {
					event.preventDefault();
					return;
				}

				if (!primaryProduct) {
					return;
				}

				/**
				 * Stop the event from propagating to prevent UFO
				 * VC90 metric from being cancelled on route change.
				 */
				event.stopPropagation();

				// Prefetch FPS results
				preloadSearchPageQuery({
					search: { searchQuery: quickFindQuery.trim() },
				});

				// Store search in recent searches
				if (quickFindQuery.trim() !== '' && user && user.id && primaryProduct) {
					addRecentSearch(quickFindQuery.trim(), user.id, primaryProduct, RECENT_SEARCH_KEY);
					updateRecentQueries?.();
				}

				event.currentTarget.blur();
				setQuickFindOpen(false);

				fireAnalyticsEvent(
					onAdvancedSearchLinkSelected({
						actionSubjectId: `${primaryProduct.toLowerCase()}AdvancedSearchLink`,
						attributes: {
							...commonAttributes,
							trigger: 'return',
							returnModified: event.metaKey || event.ctrlKey,
							newTab: false,
							isLoading:
								content?.state === State.PREQUERY_LOADING ||
								content?.state === State.POSTQUERY_LOADING,
						},
						nonPrivacySafeAttributes,
					}),
				);

				// Executing a search query on FPS search will always clear the filters in QF as FPS filters
				// is the source of truth for filter state.
				clearAllFilters();

				traceUFOPress(QUICK_FIND_CLOSE_INTERACTION, event.timeStamp);

				const onNavigateCallback =
					typeof onNavigate === 'function' ? onNavigate : onNavigate.callback;

				if (primaryProduct === PrimaryProductKeys.Jira) {
					const ctrlKey = event.metaKey || event.ctrlKey;

					// Ctrl + Enter
					// On Jira Full Page Search, Ctrl + Enter = Go to NIN
					// On other products, Ctrl + Enter = Go to FPS
					const swapQuickFindCTAs =
						isOnJiraFullPageSearch() &&
						primaryProduct &&
						primaryProduct === PrimaryProductKeys.Jira &&
						fg('jira_search_fps_cta_swap');
					if ((ctrlKey && !swapQuickFindCTAs) || (!ctrlKey && swapQuickFindCTAs)) {
						onNavigateCallback(
							generateSearchUrl(
								{
									...queryParams,
									text: quickFindQuery,
								},
								false, // This is still Confluence, so won't be a secondary link
							),
							'push',
							event,
						);
						return;
					}

					try {
						const url = getWorkItemPageUrl({
							query: quickFindQuery,
							jiraFilters,
							generateSearchUrl,
							queryParams,
						});

						onNavigateCallback(url, 'push', event);
						return;
					} catch (error) {
						// If network or runtime errors occcur, follow the default redirection behaviour.
						logException(error);
					}
				}

				const searchForPrimaryProduct =
					!selectedProduct &&
					((applicationMode === ApplicationModes.Rovo && (event.metaKey || event.ctrlKey)) ||
						(applicationMode !== ApplicationModes.Rovo && !(event.metaKey || event.ctrlKey)));
				onNavigateCallback(
					generateSearchUrl(
						{
							...queryParams,
							...(searchForPrimaryProduct ? { product: primaryProduct } : {}),
							text: quickFindQuery,
						},
						false,
					),
					'push',
				);
			}

			if (event.key === 'Escape') {
				/**
				 * Prevent IME composition from closing the quick find
				 */
				if (event.nativeEvent.isComposing) {
					event.preventDefault();
					return;
				}
				event.currentTarget.blur();
				setQuickFindOpen(false);

				traceUFOPress(QUICK_FIND_CLOSE_INTERACTION, event.timeStamp);
			}
		},
		[
			getSelectedIndex,
			primaryProduct,
			quickFindQuery,
			user,
			setQuickFindOpen,
			fireAnalyticsEvent,
			commonAttributes,
			content?.state,
			nonPrivacySafeAttributes,
			selectedProduct,
			applicationMode,
			clearAllFilters,
			onNavigate,
			generateSearchUrl,
			queryParams,
			updateRecentQueries,
			jiraFilters,
			logException,
		],
	);

	const onChange = useCallback(
		(event: React.ChangeEvent<HTMLInputElement>) => {
			const newQuery = event.currentTarget.value;
			setLocalQuery(newQuery);
			debouncedOnTextEnteredAnalyticsCallback();
		},
		[debouncedOnTextEnteredAnalyticsCallback, setLocalQuery],
	);

	const onFocus = useCallback(
		(event: React.FocusEvent<HTMLInputElement>) => {
			setQuickFindOpen(true);

			if (!quickFindOpen) {
				traceUFOPress(QUICK_FIND_OPEN_INTERACTION, event.timeStamp);
				addUFOCustomData({ queryLength: localQuery.length });
			}
		},
		[setQuickFindOpen, quickFindOpen, localQuery.length],
	);

	const Textfield = textfieldComponent || DefaultTextfield;

	return (
		<Textfield
			value={localQuery}
			dialogId={dialogId}
			inputRef={inputRef}
			quickFindOpen={quickFindOpen}
			isNav4Enabled={isNav4Enabled}
			isAdminHubAIEnabled={isAdminHubAIEnabled}
			onFocus={onFocus}
			onKeyDown={onKeyDown}
			onChange={onChange}
			onClear={() => {
				// Cancel pending debounced callback
				debouncedOnTextEnteredAnalyticsCallback.cancel();

				// Abort task if active when user clicks clear button
				// Note: We don't wrap in startTransition here because abortTask needs to
				// update state synchronously to prevent race conditions with new queries
				if (isTaskActive()) {
					abortTask('text_cleared');
				}
				setLocalQuery('');
				inputRef?.current?.focus();
			}}
			{...(fg('quickfind-ghosttext') && { placeholder: existingGhostTextData?.text || undefined })}
		/>
	);
};


